/**
 * @module utils/columnSort/syncFromQB/syncOps
 * @description 已持久化 column_sort 时 QB→面板同步（对齐旧 fieldColumnSort/crud）
 */

import { SortableType } from '@dbl/core/chartEditor';
import type {
  IColumnFilter,
  IColumnSortItem,
  IColumnSortFieldInfo,
} from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import type {
  IFieldSnapshot,
  IQBChangeSet,
  IQBReorderEvent,
} from '../../../types';
import { toFieldSnapshot } from '../../../fieldSnapshot';
import type { IBuildColumnSortFromQBInput } from '../fromQB/buildFromQB';
import { resolveCompareTimeFieldMeta } from '../fromQB/buildDimensionSortItemsFromQB';
import { shouldShowCompareUserGroup } from '../untils/compareUserGroup';
import {
  applyCompareTimeToColumnSort,
  applyUserFilterGroupToColumnSort,
} from './compareTimeUserFilter';
import {
  applyAggregatePrefix,
  hasSameDimensionField,
  isMetricAttachedVirtualColumn,
  isPercentageColumn,
  isPercentVirtualColumn,
  isSameFieldColumnSortItem,
  resolveFieldAggregate,
  resolveMetricColumnName,
  snapshotToMetricSortItem,
} from '../untils/fieldUtils';
import {
  isDimPctColumnForMetric,
  isDimPctVirtualColumn,
  parseDimPctColumn,
} from '../untils/dimensionPercentage';
import {
  COLUMN_SORT_BUILDER_DIMENSION,
  COLUMN_SORT_BUILDER_METRIC,
  dedupeColumnSortList,
  getColumnSortItemId,
  getColumnSortKey,
  isColumnSortDimension,
  isCompareUserFilterColumn,
  matchFieldToSortItem,
  normalizeColumnSortItem,
  percentVirtualColumn,
  PERCENTAGE_PREFIX,
  stripPercentagePrefix,
} from '../untils/keys';

export interface IColumnSortOpsContext {
  columnDimensions?: unknown[];
  columnMetrics?: unknown[];
  columnFilters?: unknown[];
  columnUserFilters?: unknown[];
  hasUserFilters?: boolean;
}

function isStandaloneCompareTimeColumn(item: IColumnSortItem): boolean {
  if (isColumnSortDimension(item)) {
    return false;
  }
  return item.is_compare_date === true;
}

function isSpecialDimensionPrefix(item: IColumnSortItem): boolean {
  if (isCompareUserFilterColumn(item.column)) {
    return true;
  }
  return isColumnSortDimension(item) && !!item.is_compare_date;
}

function isMovableDimension(item: IColumnSortItem): boolean {
  return isColumnSortDimension(item) && !isSpecialDimensionPrefix(item);
}

/**
 * 为「标准列序里有、用户列序里没有」的新列计算插入下标（reconcile 第 ③ 步子逻辑）。
 *
 * 术语：
 * - list：裁剪后的用户列序（pruned / result），已有列保持用户相对顺序
 * - canonical：标准列序（buildColumnSortFromQB），按当前 QB 推导的参考答案
 * - newItem：待插入的新列
 *
 * 思路：在 canonical 里找到 newItem，看其前驱/后继是否已在 list 中；
 * 插在前驱后面，或后继前面，使新列位置尽量贴近 QB 标准顺序。
 *
 * @example
 * canonical: [对照组, 日期, D1, D3]
 * list:      [对照组, 日期, D1]        ← 用户列序，缺 D3
 * 插 D3：前驱 D1 在 list 下标 2 → 返回 3（插在 D1 后）
 */
function findInsertIndexByCanonicalOrder(
  list: IColumnSortItem[],
  newItem: IColumnSortItem,
  canonical: IColumnSortItem[],
): number {
  const newId = getColumnSortItemId(newItem);
  const canonIdx = canonical.findIndex(
    row => getColumnSortItemId(row) === newId,
  );
  if (canonIdx < 0) {
    return list.length;
  }

  // 优先：在 canonical 中向前找「前驱列」，若前驱已在 list 中 → 插在其后
  for (let i = canonIdx - 1; i >= 0; i -= 1) {
    const predId = getColumnSortItemId(canonical[i]);
    for (let j = list.length - 1; j >= 0; j -= 1) {
      if (getColumnSortItemId(list[j]) === predId) {
        return j + 1;
      }
    }
  }

  // 其次：向前驱找不到时，在 canonical 中向后找「后继列」，插在其前
  for (let i = canonIdx + 1; i < canonical.length; i += 1) {
    const succId = getColumnSortItemId(canonical[i]);
    const idx = list.findIndex(row => getColumnSortItemId(row) === succId);
    if (idx >= 0) {
      return idx;
    }
  }

  // 兜底：新维度插在「第一个指标」前；新指标插在列表末尾
  if (isColumnSortDimension(newItem)) {
    const firstMet = list.findIndex(row => !isColumnSortDimension(row));
    return firstMet < 0 ? list.length : firstMet;
  }
  return list.length;
}

function fieldInfoFromSnap(snap: IFieldSnapshot): IColumnSortFieldInfo {
  return {
    table_id: snap.table_id,
    table_code: snap.table_code || 't0',
    column_name: snap.column_name,
    column_display: snap.column_display,
    column_display_en: snap.column_display_en,
    column_type: snap.column_type,
    aggregate: snap.builder_type === 'metric' ? undefined : undefined,
  };
}

function prepareDimensionSortItem(
  list: IColumnSortItem[],
  snap: IFieldSnapshot,
  ctx: IColumnSortOpsContext,
): IColumnSortItem | null {
  const fieldInfo = fieldInfoFromSnap(snap);
  const sortInfo = normalizeColumnSortItem({
    table_id: snap.table_id,
    table_code: snap.table_code || 't0',
    column: snap.column_name,
    builder_type: COLUMN_SORT_BUILDER_DIMENSION,
    column_display_cn: snap.column_display || snap.column_name,
    column_display_en: snap.column_display_en || snap.column_display,
  });

  const fieldKey = getColumnSortKey({
    ...sortInfo,
    builder_type: SortableType.Dimension,
  });

  const standaloneIndex = list.findIndex(item => {
    if (!isStandaloneCompareTimeColumn(item)) {
      return false;
    }
    return getColumnSortKey(item) === fieldKey;
  });

  if (standaloneIndex > -1) {
    return { ...sortInfo, is_compare_date: true };
  }

  const columnFilters = ctx.columnFilters as IColumnFilter[] | undefined;
  if (Array.isArray(columnFilters)) {
    const compareFilter = columnFilters.find(filter => {
      if (!filter?.compareTimes?.length) {
        return false;
      }
      return (
        filter.column_name === snap.column_name &&
        (filter.table_code || 't0') === (snap.table_code || 't0')
      );
    });
    if (compareFilter) {
      const hasSame = list.some(
        item =>
          isColumnSortDimension(item) &&
          item.is_compare_date &&
          getColumnSortKey(item) === fieldKey,
      );
      if (hasSame) {
        return null;
      }
      return { ...sortInfo, is_compare_date: true };
    }
  }

  return sortInfo;
}

function applyAggregateToMatchingMetrics(
  list: IColumnSortItem[],
  fieldInfo: IColumnSortFieldInfo,
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  const aggregate = resolveFieldAggregate(fieldInfo, ctx.columnMetrics);
  if (!aggregate) {
    return list;
  }
  return list.map(item => {
    if (isColumnSortDimension(item)) {
      return item;
    }
    if (!isSameFieldColumnSortItem(item, fieldInfo, aggregate)) {
      return item;
    }
    return {
      ...item,
      column_display_cn: applyAggregatePrefix(item.column_display_cn, aggregate),
      column_display_en: applyAggregatePrefix(item.column_display_en, aggregate),
    };
  });
}

export function deleteFieldsFromColumnSort(
  list: IColumnSortItem[],
  removedDims: IFieldSnapshot[],
  removedMets: IFieldSnapshot[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  let next = [...list];
  const columnFilters = ctx.columnFilters as IColumnFilter[] | undefined;

  removedDims.forEach(snap => {
    const fieldInfo = fieldInfoFromSnap(snap);
    const targetKey = getColumnSortKey({
      table_code: fieldInfo.table_code || 't0',
      table_id: fieldInfo.table_id,
      column: fieldInfo.column_name,
      builder_type: SortableType.Dimension,
    });

    let shouldKeepCompareTime = false;
    if (Array.isArray(columnFilters)) {
      shouldKeepCompareTime = columnFilters.some(
        f =>
          !!f.compareTimes?.length &&
          f.column_name === fieldInfo.column_name &&
          (f.table_code || 't0') === (fieldInfo.table_code || 't0'),
      );
    }

    next = next.filter(item => {
      if (!isColumnSortDimension(item)) {
        return true;
      }
      return getColumnSortKey(item) !== targetKey;
    });

    const aggregate = resolveFieldAggregate(fieldInfo, ctx.columnMetrics);
    const baseColumn = fieldInfo.column_name || '';
    const stillHasDimension = hasSameDimensionField({
      fieldInfo,
      columnSort: next,
    });

    if (!stillHasDimension && baseColumn) {
      const stripAgg = (value?: string): string => {
        if (!value) {
          return '';
        }
        if (aggregate) {
          const prefix = `${aggregate}_`;
          if (value.startsWith(prefix)) {
            return value.slice(prefix.length);
          }
        }
        const idx = value.lastIndexOf('_');
        if (idx >= 0 && value.slice(idx + 1) === baseColumn) {
          return baseColumn;
        }
        return value;
      };

      next = next.map(item => {
        if (isColumnSortDimension(item)) {
          return item;
        }
        if (!isSameFieldColumnSortItem(item, fieldInfo, aggregate)) {
          return item;
        }
        const isPct = (item.column || '').startsWith(PERCENTAGE_PREFIX) ||
          (item.column || '').startsWith('%');
        if (isPct) {
          const rawCn = (item.column_display_cn || '').slice(2).trim();
          const rawEn = (item.column_display_en || '').slice(2).trim();
          const innerCn = stripAgg(rawCn);
          const innerEn = stripAgg(rawEn);
          return {
            ...item,
            column_display_cn: innerCn ? `${PERCENTAGE_PREFIX}${innerCn}` : '',
            column_display_en: innerEn ? `${PERCENTAGE_PREFIX}${innerEn}` : '',
          };
        }
        return {
          ...item,
          column_display_cn: stripAgg(item.column_display_cn),
          column_display_en: stripAgg(item.column_display_en),
        };
      });
    }

    if (shouldKeepCompareTime) {
      const exists = next.some(
        item =>
          getColumnSortKey(item) === targetKey &&
          (isStandaloneCompareTimeColumn(item) ||
            (isColumnSortDimension(item) && item.is_compare_date)),
      );
      if (!exists) {
        next.push({
          table_id: fieldInfo.table_id || '',
          table_code: fieldInfo.table_code || 't0',
          column: fieldInfo.column_name,
          builder_type: COLUMN_SORT_BUILDER_DIMENSION,
          column_display_cn: fieldInfo.column_display || fieldInfo.column_name,
          column_display_en:
            fieldInfo.column_display_en ||
            fieldInfo.column_display ||
            fieldInfo.column_name,
          is_compare_date: true,
        });
      }
    }
  });

  removedMets.forEach(snap => {
    const fieldInfo = fieldInfoFromSnap(snap);
    const metricColumnName = resolveMetricColumnName({
      fieldInfo,
      columnSort: next,
      columnDimensions: ctx.columnDimensions,
      columnMetrics: ctx.columnMetrics,
    });
    const targetKey = getColumnSortKey({
      table_code: fieldInfo.table_code || 't0',
      table_id: fieldInfo.table_id,
      column: metricColumnName,
      builder_type: SortableType.Metric,
    });

    next = next.filter(item => {
      if (isColumnSortDimension(item)) {
        return true;
      }
      if (getColumnSortKey(item) === targetKey) {
        return false;
      }
      return !isPercentageColumn(item, metricColumnName) &&
        !isDimPctColumnForMetric(item, metricColumnName);
    });
  });

  return dedupeColumnSortList(next);
}

export function addDimensionToSort(
  list: IColumnSortItem[],
  snap: IFieldSnapshot,
  newIndex?: number,
  ctx: IColumnSortOpsContext = {},
): IColumnSortItem[] {
  let deduped = dedupeColumnSortList(list);
  const sortInfo = prepareDimensionSortItem(deduped, snap, ctx);
  if (!sortInfo) {
    return deduped;
  }

  const itemId = getColumnSortItemId(
    normalizeColumnSortItem(sortInfo),
  );
  if (deduped.some(row => getColumnSortItemId(row) === itemId)) {
    return deduped;
  }

  let specialEnd = -1;
  deduped.forEach((row, index) => {
    if (isSpecialDimensionPrefix(row)) {
      specialEnd = index;
    }
  });
  const baseInsertIndex = specialEnd + 1;

  const movableIndices: number[] = [];
  deduped.forEach((row, index) => {
    if (isMovableDimension(row)) {
      movableIndices.push(index);
    }
  });

  let insertIndex: number;
  if (movableIndices.length === 0) {
    insertIndex = baseInsertIndex;
  } else if (typeof newIndex === 'number') {
    const clamped = Math.max(0, Math.min(newIndex, movableIndices.length));
    if (clamped <= 0) {
      insertIndex = movableIndices[0];
    } else if (clamped >= movableIndices.length) {
      insertIndex = movableIndices[movableIndices.length - 1] + 1;
    } else {
      insertIndex = movableIndices[clamped];
    }
  } else {
    insertIndex = movableIndices[movableIndices.length - 1] + 1;
  }

  insertIndex = Math.max(insertIndex, baseInsertIndex);
  insertIndex = Math.min(insertIndex, deduped.length);

  const next = [...deduped];
  next.splice(insertIndex, 0, sortInfo);
  deduped = dedupeColumnSortList(next);

  return applyAggregateToMatchingMetrics(
    deduped,
    fieldInfoFromSnap(snap),
    ctx,
  );
}

export function addMetricToSort(
  list: IColumnSortItem[],
  snap: IFieldSnapshot,
  newIndex: number,
  ctx: IColumnSortOpsContext = {},
): IColumnSortItem[] {
  const deduped = dedupeColumnSortList(list);
  const item = snapshotToMetricSortItem(
    {
      table_id: snap.table_id,
      table_code: snap.table_code || 't0',
      column_name: snap.column_name,
      column_display: snap.column_display,
      column_display_en: snap.column_display_en,
      aggregate: (snap as { aggregate?: string }).aggregate,
    },
    ctx.columnDimensions,
    deduped,
  );

  if (deduped.some(row => getColumnSortItemId(row) === getColumnSortItemId(item))) {
    return deduped;
  }

  const mainMetricIndices: number[] = [];
  deduped.forEach((row, idx) => {
    if (!isColumnSortDimension(row) && !isMetricAttachedVirtualColumn(row)) {
      mainMetricIndices.push(idx);
    }
  });

  const clamped = Math.max(0, Math.min(newIndex, mainMetricIndices.length));
  let insertPos = deduped.length;
  if (mainMetricIndices.length === 0) {
    insertPos = deduped.length;
  } else if (clamped === 0) {
    insertPos = mainMetricIndices[0];
  } else {
    const prevMainIdx = mainMetricIndices[clamped - 1];
    const prevCol = deduped[prevMainIdx]?.column || '';
    let pos = prevMainIdx + 1;
    while (
      pos < deduped.length &&
      !isColumnSortDimension(deduped[pos]) &&
      (isPercentageColumn(deduped[pos], prevCol) ||
        isDimPctColumnForMetric(deduped[pos], prevCol))
    ) {
      pos += 1;
    }
    insertPos = pos;
  }

  const next = [...deduped];
  next.splice(insertPos, 0, item);
  return dedupeColumnSortList(next);
}

export function updateColumnSortInList(
  list: IColumnSortItem[],
  reorder: IQBReorderEvent,
  zone: 'dimension' | 'metric',
  ctx: IColumnSortOpsContext = {},
): IColumnSortItem[] {
  const fieldInfo = fieldInfoFromSnap(reorder.field);
  const builderType =
    zone === 'dimension' ? SortableType.Dimension : SortableType.Metric;

  const targetKey = getColumnSortKey({
    table_code: fieldInfo.table_code || 't0',
    table_id: fieldInfo.table_id,
    column:
      zone === 'metric'
        ? resolveMetricColumnName({
            fieldInfo,
            columnSort: list,
            columnDimensions: ctx.columnDimensions,
            columnMetrics: ctx.columnMetrics,
          })
        : fieldInfo.column_name,
    builder_type: builderType,
  });

  if (zone === 'metric') {
    const metricName = fieldInfo.column_name || '';
    const groupItems: IColumnSortItem[] = [];
    const remaining: IColumnSortItem[] = [];
    list.forEach(item => {
      if (
        item.builder_type === SortableType.Metric &&
        (getColumnSortKey(item) === targetKey ||
          isPercentageColumn(item, metricName) ||
          isDimPctColumnForMetric(item, metricName))
      ) {
        groupItems.push(item);
      } else {
        remaining.push(item);
      }
    });

    if (groupItems.length === 0) {
      return list;
    }

    const mainMetrics: { idx: number; item: IColumnSortItem }[] = [];
    remaining.forEach((item, idx) => {
      if (
        item.builder_type === SortableType.Metric &&
        !isMetricAttachedVirtualColumn(item)
      ) {
        mainMetrics.push({ idx, item });
      }
    });

    const clampedIndex = Math.max(
      0,
      Math.min(reorder.newIndex, mainMetrics.length),
    );

    let insertPos = 0;
    if (mainMetrics.length === 0) {
      insertPos = 0;
    } else if (clampedIndex === 0) {
      insertPos = mainMetrics[0].idx;
    } else if (clampedIndex >= mainMetrics.length) {
      insertPos = mainMetrics[mainMetrics.length - 1].idx + 1;
    } else {
      insertPos = mainMetrics[clampedIndex].idx;
    }

    const next = [...remaining];
    next.splice(insertPos, 0, ...groupItems);
    return dedupeColumnSortList(next);
  }

  const isMovableItem = (item: IColumnSortItem): boolean => {
    if (item.builder_type !== SortableType.Dimension) {
      return false;
    }
    if (isCompareUserFilterColumn(item.column)) {
      return false;
    }
    if (isStandaloneCompareTimeColumn(item)) {
      return false;
    }
    return true;
  };

  const movableIndices: number[] = [];
  const movableItems: IColumnSortItem[] = [];
  list.forEach((item, index) => {
    if (isMovableItem(item)) {
      movableIndices.push(index);
      movableItems.push(item);
    }
  });

  if (movableItems.length === 0) {
    return list;
  }

  let fromIndex = movableItems.findIndex(
    item => getColumnSortKey(item) === targetKey,
  );
  if (fromIndex < 0) {
    fromIndex = reorder.oldIndex;
  }
  if (fromIndex < 0 || fromIndex >= movableItems.length) {
    return list;
  }

  const toIndex = Math.max(
    0,
    Math.min(reorder.newIndex, movableItems.length - 1),
  );
  if (fromIndex === toIndex) {
    return list;
  }

  const nextMovable = [...movableItems];
  const [moved] = nextMovable.splice(fromIndex, 1);
  nextMovable.splice(toIndex, 0, moved);

  const nextColumnSort = [...list];
  movableIndices.forEach((index, orderIndex) => {
    nextColumnSort[index] = nextMovable[orderIndex];
  });

  return dedupeColumnSortList(nextColumnSort);
}

export function pruneColumnSort(
  list: IColumnSortItem[],
  removedDims: IFieldSnapshot[],
  removedMets: IFieldSnapshot[],
  ctx?: IColumnSortOpsContext,
): IColumnSortItem[] {
  return deleteFieldsFromColumnSort(
    list,
    removedDims,
    removedMets,
    ctx || {},
  );
}

export function migrateColumnSortIdentity(
  list: IColumnSortItem[],
  renamed: IQBChangeSet['renamed'],
): IColumnSortItem[] {
  return list.map(item => {
    const hit = renamed.find(
      r =>
        !r.displayOnly &&
        r.prevColumnName === item.column &&
        (r.field.table_code || 't0') === (item.table_code || 't0'),
    );
    if (!hit) {
      if (item.column.startsWith(PERCENTAGE_PREFIX) || item.column.startsWith('%')) {
        const base = stripPercentagePrefix(item.column);
        const hitPct = renamed.find(
          r => !r.displayOnly && r.prevColumnName === base,
        );
        if (hitPct) {
          return {
            ...item,
            column: percentVirtualColumn(hitPct.nextColumnName),
          };
        }
      }
      return item;
    }
    return {
      ...item,
      column: hit.nextColumnName,
      column_display_cn: hit.field.column_display,
      column_display_en: hit.field.column_display_en,
    };
  });
}

/**
 * QB 展示名改名（fieldRenamed）后，将 column_sort 列头文案与 QB 维/指区保持一致。
 * 有持久化列序时，维度展示名依赖本函数同步；指标通常已由 reconcile 对齐，此处再执行一次不影响结果。
 */
export function applyDisplayRename(
  list: IColumnSortItem[],
  renamed: IQBChangeSet['renamed'],
): IColumnSortItem[] {
  const displayRenames = renamed.filter(r => r.displayOnly);
  if (!displayRenames.length) {
    return list;
  }
  return list.map(item => {
    // 对比时间日期维：列头展示遵循对比时间业务规则，不随普通维度 QB 改名一并替换
    if (item.is_compare_date) {
      return item;
    }
    const hit = displayRenames.find(
      r =>
        r.prevColumnName === item.column ||
        r.prevColumnName === getColumnSortKey(item),
    );
    if (!hit) {
      return item;
    }
    if ((hit.field.table_code || 't0') !== (item.table_code || 't0')) {
      return item;
    }
    return {
      ...item,
      column_display_cn: hit.field.column_display,
      column_display_en: hit.field.column_display_en,
    };
  });
}

export function syncColumnSortFromQBChange(
  current: IColumnSortItem[],
  changeSet: IQBChangeSet,
  ctx: IColumnSortOpsContext = {},
): IColumnSortItem[] {
  let next = [...current];
  const opsCtx: IColumnSortOpsContext = {
    columnDimensions: ctx.columnDimensions,
    columnMetrics: ctx.columnMetrics,
    columnFilters: ctx.columnFilters,
    columnUserFilters: ctx.columnUserFilters,
    hasUserFilters: ctx.hasUserFilters,
  };

  if (changeSet.removedDimensions.length || changeSet.removedMetrics.length) {
    next = deleteFieldsFromColumnSort(
      next,
      changeSet.removedDimensions,
      changeSet.removedMetrics,
      opsCtx,
    );
  }

  for (const snap of changeSet.addedDimensions) {
    const raw = changeSet.addedDimensions.indexOf(snap);
    const qbIndex =
      changeSet.reorderedDimensions?.field.id === snap.id
        ? changeSet.reorderedDimensions.newIndex
        : raw;
    next = addDimensionToSort(next, snap, qbIndex, opsCtx);
  }

  for (const snap of changeSet.addedMetrics) {
    const raw = changeSet.addedMetrics.indexOf(snap);
    const qbIndex =
      changeSet.reorderedMetrics?.field.id === snap.id
        ? changeSet.reorderedMetrics.newIndex
        : raw;
    next = addMetricToSort(next, snap, qbIndex, opsCtx);
  }

  if (changeSet.reorderedDimensions) {
    next = updateColumnSortInList(
      next,
      changeSet.reorderedDimensions,
      'dimension',
      opsCtx,
    );
  }
  if (changeSet.reorderedMetrics) {
    next = updateColumnSortInList(
      next,
      changeSet.reorderedMetrics,
      'metric',
      opsCtx,
    );
  }

  const identityRenames = changeSet.renamed.filter(r => !r.displayOnly);
  if (identityRenames.length) {
    next = migrateColumnSortIdentity(next, identityRenames);
  }
  const displayRenames = changeSet.renamed.filter(r => r.displayOnly);
  if (displayRenames.length) {
    next = applyDisplayRename(next, displayRenames);
  }

  if (changeSet.events.has('userFilterGroupChanged')) {
    next = applyUserFilterGroupToColumnSort(
      next,
      ctx.columnUserFilters,
      ctx.hasUserFilters,
    );
  }

  if (changeSet.events.has('compareTimeChanged') && changeSet.compareTime) {
    next = applyCompareTimeToColumnSort(
      next,
      changeSet.compareTime,
      ctx.columnDimensions || [],
      ctx.columnFilters,
    );
  }

  return dedupeColumnSortList(next);
}

export function fieldSnapshotFromRaw(
  raw: unknown,
  builderType: 'dimension' | 'metric',
): IFieldSnapshot | null {
  return toFieldSnapshot(raw, builderType);
}

/**
 * 判断列排序项在当前 QB 下是否仍合法（reconcile 第 ① 步裁剪依据）。
 *
 * 返回 false 的项会从 persisted 中移除；特殊列规则：
 * - 对照组：用户筛选为多组对照（非「仅全部用户」）时为 true
 * - % 虚拟列：column_percentage 仍勾选且主指标仍在 QB 指标区
 * - 对比时间列：开启时 dateFieldKey 匹配；关闭时若仍对应 QB 维区字段则保留原位
 * - 普通维/指：在 columnDimensions / columnMetrics 中能 match 到同 table_code+column
 */
export function isColumnSortItemInQB(
  item: IColumnSortItem,
  qbInput: IBuildColumnSortFromQBInput,
): boolean {
  if (isCompareUserFilterColumn(item.column)) {
    return shouldShowCompareUserGroup(
      qbInput.columnUserFilters,
      qbInput.hasUserFilters,
    );
  }

  if (isPercentVirtualColumn(item)) {
    const baseCol = stripPercentagePrefix(item.column);
    const tableCode = item.table_code || 't0';
    const hasPct = (qbInput.columnPercentage || []).some(
      p => p.metric === baseCol && (p.table_code || 't0') === tableCode,
    );
    if (!hasPct) {
      return false;
    }
    return (qbInput.columnMetrics || []).some(raw =>
      matchFieldToSortItem(raw as Record<string, unknown>, {
        ...normalizeColumnSortItem(item),
        column: baseCol,
      }),
    );
  }

  if (isDimPctVirtualColumn(item)) {
    const parsed = parseDimPctColumn(item.column || '');
    if (!parsed?.metric) {
      return false;
    }
    const tableCode = item.table_code || 't0';
    const metrics = qbInput.dimensionPercentage?.metrics;
    const hasDimPct = Array.isArray(metrics) &&
      metrics.some(
        m =>
          (m.table_code || 't0') === tableCode &&
          m.metric === parsed.metric &&
          Array.isArray(m.group_by) &&
          m.group_by.length >= 1 &&
          !!m.label?.trim(),
      );
    if (!hasDimPct) {
      return false;
    }
    return (qbInput.columnMetrics || []).some(raw =>
      matchFieldToSortItem(raw as Record<string, unknown>, {
        ...normalizeColumnSortItem(item),
        column: parsed.metric,
      }),
    );
  }

  if (item.is_compare_date) {
    if (qbInput.compareTime?.enabled) {
      const dateKey = qbInput.compareTime.dateFieldKey;
      if (!dateKey) {
        return false;
      }

      const inDimensions = (qbInput.columnDimensions || []).some(raw => {
        const row = raw as Record<string, unknown>;
        const name = String(row.column_name ?? row.column ?? '');
        if (name !== dateKey) {
          return false;
        }
        return matchFieldToSortItem(row, item);
      });
      if (inDimensions) {
        return true;
      }

      // 日期仅在筛选区：独立对比时间列仍合法（与 buildDimensionSortItemsFromQB.leadingCompareTime 一致）
      const meta = resolveCompareTimeFieldMeta(
        dateKey,
        qbInput.columnDimensions || [],
        qbInput.columnFilters,
      );
      return (
        item.column === meta.column &&
        (item.table_code || 't0') === meta.table_code &&
        String(item.table_id ?? '') === String(meta.table_id ?? '')
      );
    }

    // 对比已关：原对比时间行若仍对应 QB 维区字段，保留在用户列序原位（后续 applyCompareTime 清 flag）
    if (isColumnSortDimension(item)) {
      return (qbInput.columnDimensions || []).some(raw =>
        matchFieldToSortItem(raw as Record<string, unknown>, item),
      );
    }
    return false;
  }

  const pool = isColumnSortDimension(item)
    ? qbInput.columnDimensions
    : qbInput.columnMetrics;
  return (pool || []).some(raw =>
    matchFieldToSortItem(raw as Record<string, unknown>, item),
  );
}

/**
 * reconcile 第 ④⑤ 步：把 canonical 的「展示层」写回 persisted 中同 id 的已有列。
 *
 * 为何需要单独一步：
 * - 列身份 id = table_code + table_id + column + builder_type，与聚合方式、维指是否重名无关。
 * - ①③ 步只裁剪 / 补缺，不会更新「已存在且 id 相同」的行的 column_display_cn/en。
 * - buildColumnSortFromQB（canonical）已按当前 QB 计算维指同名时的 aggregate_ 展示前缀；
 *   本函数负责把该结果同步到用户曾保存的列序上，且**不改变**用户拖拽的相对顺序。
 *
 * 处理范围：
 * 1. 对比时间维（is_compare_date）：仅合并 is_compare_date 标记（persist display 保持 QB 字段名）
 * 2. 指标主列 + 列占比 % 虚拟列：合并 column_display_cn / column_display_en
 *
 * 不处理：
 * - 普通维度列（展示名通常与 QB 一致；对比时间维走分支 1）
 * - 对照组列（DATALAB_COMPARE_USER_FILTERS）
 *
 * 覆盖场景（维指同物理字段 table_code+table_id+column）：
 * - 指标改聚合方式 → canonical 展示名变，persisted 指标行对齐
 * - 新增维度 → canonical 给指标加前缀，persisted 已有指标行对齐
 * - 删除维度 → canonical 去掉前缀，persisted 指标行对齐
 * - 新增指标（与维度同名）→ ③ 步插入 canonical 行；已有指标行亦在本步对齐
 */
function mergePersistedDisplayFromCanonical(
  result: IColumnSortItem[],
  canonical: IColumnSortItem[],
): IColumnSortItem[] {
  const canonById = new Map(
    canonical.map(item => [getColumnSortItemId(item), item]),
  );

  return result.map(item => {
    const canon = canonById.get(getColumnSortItemId(item));
    if (!canon) {
      return item;
    }

    // 对比时间：仅同步 is_compare_date，不覆盖 persisted 的 column_display_*
    if (canon.is_compare_date) {
      return normalizeColumnSortItem({
        ...item,
        is_compare_date: true,
      });
    }

    // 以下仅指标段（含 % 虚拟列）；维度列保持 persisted 展示名
    if (isColumnSortDimension(item)) {
      return item;
    }
    if (isCompareUserFilterColumn(item.column)) {
      return item;
    }
    if (isColumnSortDimension(canon)) {
      return item;
    }

    const cn = String(item.column_display_cn ?? '');
    const en = String(item.column_display_en ?? '');
    const canonCn = String(canon.column_display_cn ?? '');
    const canonEn = String(canon.column_display_en ?? '');
    if (cn === canonCn && en === canonEn) {
      return item;
    }

    return normalizeColumnSortItem({
      ...item,
      column_display_cn: canon.column_display_cn,
      column_display_en: canon.column_display_en,
    });
  });
}

/**
 * 将「用户已持久化的列序」与「当前 QB 推导的标准列序」对齐（reconcile）。
 *
 * 输入：
 * - persisted：base/draft 中的 column_sort（用户曾拖拽保存的顺序）
 * - qbInput：当前 QB 维/指/筛选/占比/对比时间，供合法性判断
 * - canonical：buildColumnSortFromQB(qbInput) 的标准列序（参考答案）
 *
 * 输出：对齐后的列序。原则：尽量保留用户对「已有列」的相对顺序；
 * 删除 QB 已不存在的列；补上 QB 新增而用户列序中缺失的列。
 *
 * 列身份 id = table_code + table_id + column + builder_type（见 getColumnSortItemId）。
 *
 * ---
 * 第 ① 步【裁剪 prune】
 * 从 persisted 逐列问 isColumnSortItemInQB：QB 删维/删指、关对比时间、关对照组、
 * 取消列占比等 → 对应项剔除。不调整剩余列的先后顺序。
 *
 * @example 第 ① 步
 * persisted: [对照组, 日期, D1, D2]
 * QB 已删 D2 → pruned: [对照组, 日期, D1]
 *
 * ---
 * 第 ② 步【裁光则整表替换】
 * 若 pruned 为空（用户列序全部失效），直接返回 canonical，不再保留用户顺序。
 *
 * ---
 * 第 ③ 步【补缺 insert】
 * 遍历 canonical，对 pruned/result 中尚不存在的列：
 * 用 findInsertIndexByCanonicalOrder 算下标后 splice 插入。
 * 已有列仍在 result 中保持第 ① 步后的相对顺序（不会因 QB 区内拖拽而重排已有列）。
 *
 * @example 第 ③ 步
 * pruned:   [对照组, 日期, D1]
 * canonical:[对照组, 日期, D1, D3]  ← QB 新加 D3
 * → 在 D1 后插入 D3 → result: [对照组, 日期, D1, D3]
 *
 * ---
 * 第 ④⑤ 步【展示名对齐 mergePersistedDisplayFromCanonical】
 * 同 id 的 persisted 行从 canonical 同步 column_display_cn/en（对比时间维仅同步 is_compare_date）。
 * 维指同名字段增删维、改 aggregate 时 id 不变，依赖本步刷新指标列头展示。
 *
 * @example 第 ④⑤ 步
 * persisted 指标行展示名「销售额」；canonical 因与维度同名 →「sum_销售额」
 * → 合并后 persisted 顺序不变，展示名变为「sum_销售额」
 *
 * 注意：若仅 QB 重排已有列（无增删、展示名已一致），第 ①③④⑤ 步可能不改变输出，
 * sanitize handler 可能因 early-exit 不写回 column_sort；指/维重排由 reorder handler 处理。
 */
export function reconcileColumnSortWithQB(
  persisted: IColumnSortItem[],
  qbInput: IBuildColumnSortFromQBInput,
  canonical: IColumnSortItem[],
): IColumnSortItem[] {
  // 第 ① 步：裁剪 — 去掉 QB 中已不存在或不合法的列
  const pruned = dedupeColumnSortList(
    persisted
      .filter(item => isColumnSortItemInQB(item, qbInput))
      .map(item => normalizeColumnSortItem(item)),
  );

  // 第 ② 步：用户列序全部无效 → 整表采用标准列序
  if (!pruned.length) {
    return canonical;
  }

  // 第 ③ 步：补缺 — 标准列序里有、裁剪结果里没有的列，按标准相对位置插入
  const used = new Set(pruned.map(getColumnSortItemId));
  const result = [...pruned];

  for (const item of canonical) {
    const id = getColumnSortItemId(item);
    if (used.has(id)) {
      continue;
    }
    const insertAt = findInsertIndexByCanonicalOrder(result, item, canonical);
    result.splice(insertAt, 0, normalizeColumnSortItem(item));
    used.add(id);
  }

  // 第 ④⑤ 步：维指同名指标（含 % 列）展示名与 canonical 对齐；对比时间维仅同步 is_compare_date
  const merged = mergePersistedDisplayFromCanonical(result, canonical);

  return dedupeColumnSortList(merged);
}
